import { motion } from "framer-motion";
import { 
  ClipboardList, 
  Palette, 
  TestTube, 
  Headphones 
} from "lucide-react";

const Process = () => {
  const steps = [
    {
      icon: ClipboardList,
      number: "01",
      title: "Requirement Analysis",
      description: "We understand your business needs and define project scope"
    },
    {
      icon: Palette,
      number: "02",
      title: "Design & Development",
      description: "Creating solutions with modern technologies and best practices"
    },
    {
      icon: TestTube,
      number: "03",
      title: "Testing & Deployment",
      description: "Rigorous testing ensures quality before going live"
    },
    {
      icon: Headphones,
      number: "04",
      title: "Support & Scaling",
      description: "Ongoing maintenance and scaling as your business grows"
    }
  ];

  return (
    <section 
      className="relative py-24 sm:py-32 overflow-hidden"
      data-testid="process-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0F172A]/30" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
            Our Process
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
            How We Work
          </h2>
          <p className="text-lg text-slate-300">
            A structured approach to deliver exceptional results on every project.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Horizontal Line (desktop) */}
          <div className="hidden lg:block absolute top-[60px] left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />

          {/* Steps Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                {/* Connector Line (mobile) */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden absolute left-[27px] top-[60px] w-0.5 h-full bg-gradient-to-b from-blue-500/30 to-transparent" />
                )}

                {/* Node */}
                <div className="relative flex flex-col items-center text-center">
                  {/* Glowing Node */}
                  <div className="timeline-node relative z-10 mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#0F172A] border-2 border-blue-500/50 flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-blue-400" />
                    </div>
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-md" />
                  </div>

                  {/* Number */}
                  <span className="text-5xl font-black text-blue-600/20 mb-2">
                    {step.number}
                  </span>

                  {/* Content */}
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400 max-w-[250px]">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
